import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Container,
  Paper,
  Typography,
  Grid,
  Divider,
  Button,
  Chip,
  CircularProgress,
  Alert,
  IconButton,
  Tooltip,
} from "@mui/material";
import {
  Download as DownloadIcon,
  Print as PrintIcon,
  ArrowBack as ArrowBackIcon,
  Receipt as ReceiptIcon,
  Business as BusinessIcon,
  Person as PersonIcon,
  CalendarToday as CalendarIcon,
  Payment as PaymentIcon,
  AccountBalance as BankIcon,
} from "@mui/icons-material";
import apiService from "../../services/api";
import { COMPANY_HEADER } from "../../constants";
import "./invoice.css";

const Invoice = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchInvoiceDetails();
  }, [id]);

  const fetchInvoiceDetails = async () => {
    try {
      setLoading(true);
      const response = await apiService.get(`/subscriptions/invoice/${id}`);
      console.log("Invoice", response);
      if (response.success) {
        console.log("Invoice", response.data);
        setInvoice(response.data);
      } else {
        setError(response.message || "Failed to fetch invoice details");
      }
    } catch (error) {
      console.error("Error fetching invoice:", error);
      setError("Failed to fetch invoice details");
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = () => {
    window.print();
  };

    const handleDownload = () => {
    // Create a properly formatted PDF version
    const printWindow = window.open("", "_blank");
    const issuedName = invoice?.invoice_name || invoice?.tenant_name || invoice?.customer_name || "";
    const issuedPhone = invoice?.invoice_phone || invoice?.tenant_phone || invoice?.customer_phone || "";
    const issuedEmail = invoice?.invoice_email || invoice?.tenant_email || invoice?.customer_email || "";
    const issuedAddr1 =
      invoice?.invoice_address_line1 ||
      invoice?.tenant_address_line1 ||
      invoice?.tenant_address ||
      "";
    const issuedAddr2 = invoice?.invoice_address_line2 || invoice?.tenant_address_line2 || "";
    const issuedCity = invoice?.invoice_city || invoice?.tenant_city || "";
    const issuedState =
      invoice?.invoice_state ||
      invoice?.tenant_invoice_state_name ||
      invoice?.tenant_state_name ||
      invoice?.tenant_state ||
      "";
    const issuedZip = invoice?.invoice_zip_code || invoice?.tenant_zip_code || invoice?.tenant_zip || "";
    const issuedCityStateZip = [issuedCity, issuedState, issuedZip].filter(Boolean).join(" ");
    const gstName = invoice?.tenant_company_name || "";
    const gstNumber = invoice?.tenant_gst_number || "";
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>${COMPANY_HEADER.NAME} Invoice - ${invoice?.subscription_number}</title>
          <style>
            body { 
              font-family: Arial, sans-serif; 
              margin: 0; 
              padding: 20px; 
              background: white;
              color: #333;
            }
            .invoice-container { 
              max-width: 800px; 
              margin: 0 auto; 
              background: white;
            }
            .company-header { 
              text-align: center; 
              margin-bottom: 30px; 
              padding: 20px;
              border-bottom: 2px solid #1976d2;
            }
            .company-name { 
              font-size: 28px; 
              font-weight: bold; 
              color: #1976d2; 
              margin-bottom: 10px;
            }
            .company-tagline { 
              font-size: 16px; 
              color: #666; 
              margin-bottom: 15px;
            }
            .company-details { 
              font-size: 14px; 
              color: #666; 
              line-height: 1.6;
            }
            .invoice-title-section { 
              display: flex; 
              justify-content: space-between; 
              margin-bottom: 30px; 
              padding: 20px 0;
            }
            .invoice-title { 
              font-size: 24px; 
              font-weight: bold; 
              color: #1976d2;
            }
            .invoice-number { 
              text-align: right;
            }
            .invoice-number h3 { 
              margin: 0 0 10px 0; 
              color: #333;
            }
            .invoice-number p { 
              margin: 5px 0; 
              color: #666;
            }
            .issued-to-section { 
              display: flex; 
              justify-content: space-between; 
              margin-bottom: 30px; 
              padding: 20px 0;
            }
            .issued-to-left { 
              flex: 1;
            }
            .issued-to-right { 
              flex: 1; 
              text-align: right;
            }
            .issued-to-title { 
              font-size: 18px; 
              font-weight: bold; 
              color: #333; 
              margin-bottom: 10px;
            }
            .issued-to-name { 
              font-size: 16px; 
              font-weight: bold; 
              color: #1976d2; 
              margin-bottom: 8px;
            }
            .issued-to-details { 
              font-size: 14px; 
              color: #666; 
              line-height: 1.4;
              margin-bottom: 5px;
            }
            .company-info-right { 
              text-align: right;
            }
            .company-info-right h3 { 
              margin: 0 0 10px 0; 
              color: #333;
            }
            .company-info-right p { 
              margin: 5px 0; 
              color: #666;
            }
            .items-table { 
              width: 100%; 
              border-collapse: collapse; 
              margin: 30px 0; 
              border: 1px solid #ddd;
            }
            .items-table th { 
              background-color: #f5f5f5; 
              padding: 15px 12px; 
              text-align: left; 
              font-weight: bold; 
              color: #333; 
              border-bottom: 2px solid #ddd;
            }
            .items-table td { 
              padding: 15px 12px; 
              border-bottom: 1px solid #f0f0f0;
            }
            .total-section { 
              text-align: right; 
              margin: 30px 0; 
            }
            .total-breakdown { 
              display: inline-block; 
              min-width: 350px; 
              background: #f8f9fa; 
              padding: 20px; 
              border-radius: 8px; 
              border: 1px solid #e9ecef;
            }
            .total-row { 
              display: flex; 
              justify-content: space-between; 
              margin-bottom: 10px;
            }
            .total-divider { 
              margin: 15px 0; 
              border: none; 
              border-top: 2px solid #e9ecef;
            }
            .total-final { 
              font-size: 18px; 
              font-weight: bold; 
              color: #1976d2;
            }
            .footer { 
              text-align: center; 
              margin-top: 50px; 
              padding-top: 20px; 
              border-top: 1px solid #e0e0e0; 
              color: #666;
            }
            .footer-message { 
              font-size: 16px; 
              margin-bottom: 8px;
            }
            .footer-contact { 
              font-size: 14px; 
              margin-bottom: 15px;
            }
            .footer-note { 
              font-size: 12px; 
              opacity: 0.8; 
              font-style: italic;
            }
            @media print { 
              body { -webkit-print-color-adjust: exact; }
              .invoice-container { margin: 0; }
            }
          </style>
        </head>
        <body>
          <div class="invoice-container">
            <!-- Invoice Title and Number -->
            <div class="invoice-title-section">
              <div>
                <div class="invoice-title">TAX INVOICE</div>
              </div>
              <div class="invoice-number">
                <h3>Invoice #${invoice?.subscription_number}</h3>
                <p>Date: ${new Date(invoice?.created_at).toLocaleDateString("en-IN")}</p>
              </div>
            </div>

            <!-- Issued To Section -->
            <div class="issued-to-section">
              <div class="issued-to-left">
                <div class="issued-to-title">Issued To</div>
                <div class="issued-to-name">
                  ${issuedName}
                </div>
                <div class="issued-to-details">
                  ${issuedPhone} ${issuedPhone && issuedEmail ? "|" : ""} ${issuedEmail}
                </div>
                <div class="issued-to-details">
                  ${issuedAddr1}
                </div>
                <div class="issued-to-details">
                  ${issuedAddr2}
                </div>
                <div class="issued-to-details">
                  ${issuedCityStateZip}
                </div>
                <div class="issued-to-details">
                  GST Name: ${gstName}
                </div>
                <div class="issued-to-details">
                  GST Number: ${gstNumber}
                </div>
              </div>
              
              <div class="company-info-right">
                <h3>${COMPANY_HEADER.NAME}</h3>
                <p>Email: ${COMPANY_HEADER.EMAIL}</p>
                <p>Website: ${COMPANY_HEADER.WEBSITE}</p>
                <p style="font-weight: bold;">GST Number: ${COMPANY_HEADER.GST_NUMBER}</p>
              </div>
            </div>

            <!-- Invoice Items Table -->
            <table class="items-table">
              <thead>
                <tr>
                  <th>Description</th>
                  <th style="text-align: right;">Amount (₹)</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>
                    <div style="font-weight: bold; margin-bottom: 5px;">
                      ${invoice?.plan_name || 'Subscription Plan'} membership from 
                      ${new Date(invoice?.start_date).toLocaleDateString("en-IN")} to 
                      ${new Date(invoice?.end_date || invoice?.expires_at).toLocaleDateString("en-IN")}
                    </div>
                    <div style="color: #666; font-size: 14px;">
                      ${invoice?.billing_cycle || 'Monthly'} subscription
                    </div>
                  </td>
                  <td style="text-align: right; font-weight: bold;">
                    ₹${invoice?.price || 0}
                  </td>
                </tr>
              </tbody>
            </table>

            <!-- Total Section -->
            <div class="total-section">
              <div class="total-breakdown">
                <div class="total-row">
                  <span>Offer Amount:</span>
                  <span>₹${ parseInt(invoice?.offer_price || 0).toFixed(2) || 0}</span>
                </div>
                <div class="total-row">
                  <span>Discount Amount:</span>
                  <span>- ₹${invoice?.discount_amount || 0}</span>
                </div>
                <div class="total-row">
                  <span>Base Amount:</span>
                  <span>₹${ parseInt(invoice?.price || 0).toFixed(2) || 0}</span>
                </div>
                <div class="total-row">
                  <span>GST (18%):</span>
                  <span>+ ₹${invoice?.tax_amount || 0}</span>
                </div>
                <div class="total-divider"></div>
                <div class="total-row total-final">
                  <span>Total Amount:</span>
                  <span>₹${invoice?.total_amount || 0}</span>
                </div>
              </div>
            </div>
 
          </div>
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.print();
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "60vh",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Button
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate("/subscriptions/invoices")}
        >
          Back to Subscriptions
        </Button>
      </Container>
    );
  }

  if (!invoice) {
    return (
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Alert severity="warning" sx={{ mb: 2 }}>
          Invoice not found
        </Alert>
        <Button
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate("/subscriptions/invoices")}
        >
          Back to Subscriptions
        </Button>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      {/* Header Actions */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <Button
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate("/subscriptions/invoices")}
        >
          Back to Subscriptions
        </Button>

        <Box sx={{ display: "flex", gap: 2 }}>
          <Tooltip title="Download / Print Invoice">
            <IconButton
              onClick={handleDownload}
              sx={{
                bgcolor: "primary.main",
                color: "white",
                "&:hover": { bgcolor: "primary.dark" },
              }}
            >
              <PrintIcon />
            </IconButton>
          </Tooltip>

          {/* <Tooltip title="Print Invoice">
            <IconButton
              onClick={handlePrint}
              sx={{
                bgcolor: "success.main",
                color: "white",
                "&:hover": { bgcolor: "success.dark" },
              }}
            >
              <PrintIcon />
            </IconButton>
          </Tooltip> */}
        </Box>
      </Box>

      {/* Invoice Content */}
      <Paper
        id="invoice-content"
        elevation={3}
        sx={{
          p: 4,
          "@media print": {
            boxShadow: "none",
            p: 2,
          },
        }}
      >
        {/* Invoice Title and Number */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            mb: 4,
          }}
        >
          <Box>
            <Typography
              variant="h4"
              sx={{ fontWeight: "bold", color: "primary.main", mb: 1 }}
            >
              TAX INVOICE
            </Typography>
          </Box>

          <Box sx={{ textAlign: "right" }}>
            <Typography variant="h6" sx={{ fontWeight: "bold", mb: 1 }}>
              Invoice #{invoice.subscription_number}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Date: {new Date(invoice.created_at).toLocaleDateString("en-IN")}
            </Typography>
          </Box>
        </Box>

        <Divider sx={{ mb: 4 }} />

        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            mb: 4,
          }}
        >
          <Box>
            <Typography
              variant="h6"
              sx={{ fontWeight: "bold", color: "text.secondary", mb: 1 }}
            >
              Issued To
            </Typography>
            <Typography
              variant="h6"
              sx={{ fontWeight: "bold", color: "text.secondary", mb: 1 }}
            >
              {invoice.invoice_name || invoice.tenant_name || invoice.customer_name}
            </Typography>
            <Typography variant="body2" color="text.secondary">            
              {(invoice.invoice_phone || invoice.tenant_phone || invoice.customer_phone || '')}
              {(invoice.invoice_phone || invoice.tenant_phone || invoice.customer_phone) && (invoice.invoice_email || invoice.tenant_email || invoice.customer_email) ? ' | ' : ''}
              {(invoice.invoice_email || invoice.tenant_email || invoice.customer_email || '')}
            </Typography>
 

            <Typography variant="body2" color="text.secondary">            
              {invoice.invoice_address_line1 || invoice.tenant_address_line1 || invoice.tenant_address || ''}
            </Typography> <Typography variant="body2" color="text.secondary">            
              {invoice.invoice_address_line2 || invoice.tenant_address_line2 || ''}
            </Typography> <Typography variant="body2" color="text.secondary">            
              {[invoice.invoice_city || invoice.tenant_city || '',
                invoice.invoice_state || invoice.tenant_invoice_state_name || invoice.tenant_state_name || invoice.tenant_state || '',
                invoice.invoice_zip_code || invoice.tenant_zip_code || invoice.tenant_zip || ''
              ].filter(Boolean).join(' ')}
            </Typography>
            <Typography variant="body2" color="text.secondary">            
           GST Name: {invoice.tenant_company_name  
                ? invoice.tenant_company_name
                : ''}
            </Typography> 
            <Typography variant="body2" color="text.secondary">            
          GST Number:  {invoice.tenant_gst_number  
                ? invoice.tenant_gst_number
                : ''}
            </Typography> 
          </Box>

          <Box sx={{ textAlign: "right" }}>
            <Typography variant="h6" sx={{ fontWeight: "bold", mb: 1 }}>
              {COMPANY_HEADER.NAME}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Email: {COMPANY_HEADER.EMAIL}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Website: {COMPANY_HEADER.WEBSITE}
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ fontWeight: "medium" }}
            >
              GST Name: {COMPANY_HEADER.GST_NAME}
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ fontWeight: "medium" }}
            >
              GST Number: {COMPANY_HEADER.GST_NUMBER}
            </Typography>
          </Box>
        </Box>
  

        {/* Invoice Items Table */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" sx={{ fontWeight: "bold", mb: 2 }}>
            Invoice Items
          </Typography>
          <Box sx={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ borderBottom: "2px solid #e0e0e0" }}>
                  <th
                    style={{
                      padding: "12px",
                      textAlign: "left",
                      fontWeight: "bold",
                      backgroundColor: "#f5f5f5",
                    }}
                  >
                    Description
                  </th>
                  <th
                    style={{
                      padding: "12px",
                      textAlign: "right",
                      fontWeight: "bold",
                      backgroundColor: "#f5f5f5",
                    }}
                  >
                    Amount (₹)
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr style={{ borderBottom: "1px solid #f0f0f0" }}>
                  <td style={{ padding: "12px" }}>
                    <Typography variant="body1" sx={{ fontWeight: "medium" }}>
                      {invoice.plan_name || "Subscription Plan"}   membership from   {new Date(invoice.start_date).toLocaleDateString("en-IN")} to  {new Date(
                  invoice.end_date || invoice.expires_at
                ).toLocaleDateString("en-IN")}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {invoice.billing_cycle || "Monthly"} subscription
                    </Typography>
                  </td>
                  <td style={{ padding: "12px", textAlign: "right" }}>
                    <Typography variant="body1" sx={{ fontWeight: "medium" }}>
                      ₹{invoice.price || 0}
                    </Typography>
                  </td>
                </tr>
              </tbody>
            </table>
          </Box>
        </Box>

        {/* Total Section */}
        <Box sx={{ textAlign: "right", mb: 4 }}>
          <Box sx={{ display: "inline-block", minWidth: "300px" }}>
            <Box
              sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}
            >
              <Typography variant="body1">Base Amount:</Typography>
              <Typography variant="body1">
                ₹{ parseInt(invoice.offer_price || 0).toFixed(2) || 0}
              </Typography>
            </Box>
            <Box
              sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}
            >
              <Typography variant="body1">Discount Amount:</Typography>
              <Typography variant="body1">
                - ₹{invoice.discount_amount || 0}
              </Typography>
            </Box>
            <Box
              sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}
            >
              <Typography variant="body1">Base Amount:</Typography>
              <Typography variant="body1">
                ₹{invoice.payment_amount || 0}
              </Typography>
            </Box>
            {invoice.tenant_invoice_state_name === COMPANY_HEADER.STATE_NAME ? (
              <>
                <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
                  <Typography variant="body1">CGST (9%):</Typography>
                  <Typography variant="body1">
                   + ₹{((invoice.tax_amount || 0) / 2).toFixed(2)}
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
                  <Typography variant="body1">SGST (9%):</Typography>
                  <Typography variant="body1">
                    + ₹{((invoice.tax_amount || 0) / 2).toFixed(2)}
                  </Typography>
                </Box>
              </>
            ) : (
              <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
                <Typography variant="body1">IGST (18%):</Typography>
                <Typography variant="body1">
                 + ₹{invoice.tax_amount || 0}
                </Typography>
              </Box>
            )}
            <Divider sx={{ my: 1 }} />
            <Box sx={{ display: "flex", justifyContent: "space-between" }}>
              <Typography
                variant="h6"
                sx={{ fontWeight: "bold", color: "primary.main" }}
              >
                Total Amount:
              </Typography>
              <Typography
                variant="h6"
                sx={{ fontWeight: "bold", color: "primary.main" }}
              >
                ₹{invoice.total_amount || 0}
              </Typography>
            </Box>
          </Box>
        </Box>
 
      </Paper>
    </Container>
  );
};

export default Invoice;
